import React, {useEffect} from 'react'
import {SlotComponent} from '../../types/ui'
import {Markdown} from './Markdown'
import {Stack, Text, Box} from '../../vanilla'
import {Sprinkles} from '../../vanilla/sprinkles.css'
import {TabPanel, Tabs} from '../tabs'
import {tableText, richTextImg} from './styles.css'
import { ContentTab, TextAlignment } from '../../types/cms'
import { useContentStore } from '../../store/hooks/useStore'
import {observer} from 'mobx-react'
export interface ContentTabGroupCmsContent {
  headline: string
  headingAlignment: Exclude<TextAlignment, 'Right'>
  tabList: ContentTab[]
}

export const ContentTabGroup: SlotComponent<ContentTabGroupCmsContent> = observer(({content}) => {
  const {headline, headingAlignment, tabList, _meta} = content
  const cms = useContentStore()

  const deliveryIds = React.useMemo(
    () => tabList
      ?.map(({_meta}) => _meta?.deliveryId)
      .filter((deliveryId, index): deliveryId is string => Boolean(deliveryId) && tabList[index].enableLastPublishedDate) ?? [],
    [tabList]
  )

  useEffect(() => {
    if (!deliveryIds.length) return

    const missingIds = deliveryIds.filter((deliveryId) => !(deliveryId in cms.lastPublishedDates))

    if (!missingIds.length) return

    Promise.allSettled(missingIds.map((deliveryId) => cms.fetchItemLastPublishedDate(deliveryId)))
  }, [deliveryIds, cms.lastPublishedDates])

  if (!tabList.length) return null

  return (
    <Stack
      className="content-tab-group"
      align="flex-start"
      paddingX="28px"
      paddingBottom="28px"
      paddingTop="16px"
      spacing="0px"
      bg="white"
    >
      {headline && (
        <Box width="full" borderBottom="1px" borderColor="gray200" paddingY="16px">
          <Text
            variant="heading1"
            lineHeight="base"
            width="full"
            as="h2"
            align={headingAlignment.toLowerCase() as Sprinkles['textAlign']}
          >
            {headline}
          </Text>
        </Box>
      )}
      <Tabs id={_meta.deliveryId} mobileLayoutAllowed>
        {tabList.map(({tabTitle, tabText, tabID, _meta}) => {
          const deliveryId = _meta?.deliveryId
          const lastPublishedDate = deliveryId ? cms.lastPublishedDates[deliveryId] : undefined
          const hasLastPublishedDate = Boolean(lastPublishedDate)
          return (
            <TabPanel key={tabID} id={tabID} title={tabTitle}>
                {hasLastPublishedDate && (
                  <Text variant="text4" color="gray400" marginTop="16px">
                    {tabTitle} was last amended on {lastPublishedDate}
                  </Text>
                )}
              <Box paddingTop="48px">
                <Markdown
                  markdown={tabText}
                  componentProps={{
                    p: {
                      marginBottom: '16px',
                      variant: 'text3',
                    },
                    h1: {
                      marginBottom: '8px',
                      lineHeight: 'tall',
                      variant: 'unstyled',
                      fontSize: 'xl',
                      fontWeight: 'bold',
                      as: 'h3',
                    },
                    table: {
                      display: 'block',
                      marginBottom: '16px',
                      overflowX: 'auto'
                    },
                    th: {
                      border: '2px',
                      borderColor: 'black',
                      borderStyle: 'solid',
                      padding: '10px',
                      className: tableText,
                      variant: 'text3'
                    },
                    td: {
                      border: '2px',
                      borderColor: 'black',
                      borderStyle: 'solid',
                      padding: '10px',
                      className: tableText,
                      variant: 'text3'
                    },
                    img: {
                      className: richTextImg
                    },
                    a: {
                      variant: 'text3',
                      textDecoration: 'underline',
                    }
                  }}
                />
              </Box>
            </TabPanel>
          )
        })}
      </Tabs>
    </Stack>
  )
})
